import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Stage, Layer, Rect, Group, Text, Line, Circle, Image as KonvaImage, RegularPolygon } from 'react-konva';
import axios from 'axios';
import SEO from '../components/SEO';
import { ArrowLeft, RotateCw, Trash2, Save, Upload, Eye, EyeOff, ZoomIn, ZoomOut, Maximize, Copy, Undo2, Redo2, Ruler, Sparkles, FileDown, X, Check, Plus, Minus, Crosshair } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCALES = ['1:50', '1:100', '1:200'];

const CATEGORY_COLORS = {
  carousel: { fill: 'rgba(59, 130, 246, 0.12)', stroke: '#3b82f6', label: '#2563eb', bg: '#eff6ff' },
  linear: { fill: 'rgba(16, 185, 129, 0.10)', stroke: '#10b981', label: '#059669', bg: '#ecfdf5' },
  compactor: { fill: 'rgba(245, 158, 11, 0.10)', stroke: '#f59e0b', label: '#d97706', bg: '#fffbeb' },
  chute: { fill: 'rgba(139, 92, 246, 0.10)', stroke: '#8b5cf6', label: '#7c3aed', bg: '#f5f3ff' },
  bin: { fill: 'rgba(100, 116, 139, 0.08)', stroke: '#64748b', label: '#475569', bg: '#f8fafc' },
};

/* ─── Sidebar mini-diagram SVGs ─── */
const EquipmentDiagram = ({ category, width, height }) => {
  const w = 40, h = 40;
  const aspect = width && height ? width / height : 1;
  const boxW = Math.min(28, 28 * Math.min(aspect, 1.8));
  const boxH = Math.min(28, 28 / Math.max(aspect, 0.6));
  const cx = w / 2, cy = h / 2;

  if (category === 'carousel') {
    const r = Math.min(boxW, boxH) / 2 + 2;
    const numBins = 4; // Default visual representation
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={2} fill="#3b82f6" opacity={0.4} />
        {/* Bin outlines arranged radially */}
        {[0, 90, 180, 270].map((angle, i) => {
          const rad = (angle - 90) * Math.PI / 180;
          const bx = cx + (r * 0.55) * Math.cos(rad);
          const by = cy + (r * 0.55) * Math.sin(rad);
          const bw = r * 0.45, bh = r * 0.38;
          return <rect key={i} x={bx - bw/2} y={by - bh/2} width={bw} height={bh} rx={1.5}
            fill="none" stroke="#3b82f6" strokeWidth="0.7" opacity={0.5}
            transform={`rotate(${angle}, ${bx}, ${by})`} />;
        })}
        {/* Rotation arrow */}
        <path d={`M ${cx + r + 3} ${cy - 2} A ${r + 3} ${r + 3} 0 0 1 ${cx + 2} ${cy - r - 3}`} fill="none" stroke="#93c5fd" strokeWidth="1" />
        <polygon points={`${cx + 1},${cy - r - 6} ${cx + 4},${cy - r - 3} ${cx - 1},${cy - r - 3}`} fill="#93c5fd" />
      </svg>
    );
  }

  if (category === 'linear') {
    const x1 = cx - boxW / 2, y1 = cy - boxH / 2;
    const numBins = 3;
    const binGap = 1;
    const binPad = 2;
    const totalBinW = boxW - binPad * 2 - binGap * (numBins - 1);
    const binW = totalBinW / numBins;
    const binH = boxH - 4;
    const binY = y1 + 2;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
        <rect x={x1} y={y1} width={boxW} height={boxH} rx={2} fill="none" stroke="#10b981" strokeWidth="1.5" />
        {/* Track rails (top and bottom) */}
        <line x1={x1 + 1} y1={y1 + 1} x2={x1 + boxW - 1} y2={y1 + 1} stroke="#10b981" strokeWidth="0.6" opacity={0.3} />
        <line x1={x1 + 1} y1={y1 + boxH - 1} x2={x1 + boxW - 1} y2={y1 + boxH - 1} stroke="#10b981" strokeWidth="0.6" opacity={0.3} />
        {/* Tall bin outlines */}
        {Array.from({ length: numBins }).map((_, i) => {
          const bx = x1 + binPad + i * (binW + binGap);
          return <rect key={i} x={bx} y={binY} width={binW} height={binH} rx={1} fill="none" stroke="#10b981" strokeWidth="0.7" opacity={0.5} />;
        })}
        {/* Direction arrow pointing LEFT (thin end / collection) */}
        <polygon points={`${x1 - 4},${cy} ${x1 + 1},${cy - 3.5} ${x1 + 1},${cy + 3.5}`} fill="#10b981" opacity={0.7} />
      </svg>
    );
  }

  if (category === 'chute') {
    const cw = 12, ch = 26;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
        <rect x={cx - cw / 2} y={cy - ch / 2} width={cw} height={ch} rx={1.5} fill="none" stroke="#8b5cf6" strokeWidth="1.5" />
        {/* Down arrow inside */}
        <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke="#8b5cf6" strokeWidth="1" opacity={0.5} />
        <polygon points={`${cx},${cy + 8} ${cx - 3},${cy + 4} ${cx + 3},${cy + 4}`} fill="#8b5cf6" opacity={0.5} />
        {/* Opening at top */}
        <line x1={cx - cw / 2 - 2} y1={cy - ch / 2} x2={cx + cw / 2 + 2} y2={cy - ch / 2} stroke="#8b5cf6" strokeWidth="1" strokeLinecap="round" />
      </svg>
    );
  }

  if (category === 'compactor') {
    const x1 = cx - boxW / 2, y1 = cy - boxH / 2;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
        <rect x={x1} y={y1} width={boxW} height={boxH} rx={2} fill="none" stroke="#f59e0b" strokeWidth="1.5" />
        {/* Compression arrows */}
        <polygon points={`${cx},${y1 + 4} ${cx - 3},${y1 + 8} ${cx + 3},${y1 + 8}`} fill="#f59e0b" opacity={0.6} />
        <polygon points={`${cx},${y1 + boxH - 4} ${cx - 3},${y1 + boxH - 8} ${cx + 3},${y1 + boxH - 8}`} fill="#f59e0b" opacity={0.6} />
      </svg>
    );
  }

  // Default (bin)
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="flex-shrink-0">
      <rect x={cx - 7} y={cy - 9} width={14} height={18} rx={2} fill="none" stroke="#64748b" strokeWidth="1.5" />
      <line x1={cx - 5} y1={cy - 9} x2={cx - 3} y2={cy - 12} stroke="#64748b" strokeWidth="1" />
      <line x1={cx + 5} y1={cy - 9} x2={cx + 3} y2={cy - 12} stroke="#64748b" strokeWidth="1" />
      <line x1={cx - 3} y1={cy - 12} x2={cx + 3} y2={cy - 12} stroke="#64748b" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
};

const WasteRoomPlanner = () => {
  const { roomId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const containerRef = useRef(null);

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [planImage, setPlanImage] = useState(null);

  const [roomScale, setRoomScale] = useState('1:100');
  const [roomWidthMm, setRoomWidthMm] = useState('');
  const [roomDepthMm, setRoomDepthMm] = useState('');
  const [showSetup, setShowSetup] = useState(false);

  const [equipment, setEquipment] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('Elephants Foot');
  const [suppliers, setSuppliers] = useState([]);
  const [placedItems, setPlacedItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const [analysisFlags, setAnalysisFlags] = useState([]);
  const [violationItemIds, setViolationItemIds] = useState([]);
  const [analysing, setAnalysing] = useState(false);
  const [showClearanceZones, setShowClearanceZones] = useState(true);

  // Undo/Redo history
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const skipHistoryRef = useRef(false);

  // Measure tool
  const [measureMode, setMeasureMode] = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);

  // Two-point calibration
  const [calibrateMode, setCalibrateMode] = useState(false);
  const [calibratePoints, setCalibratePoints] = useState([]);
  const [calibrateDistanceMm, setCalibrateDistanceMm] = useState('');
  const [calibrationData, setCalibrationData] = useState(null); // { point1, point2, distanceMm, pxPerMm }

  // AI Optimisation
  const [optimising, setOptimising] = useState(false);
  const [layouts, setLayouts] = useState(null);
  const [showLayouts, setShowLayouts] = useState(false);

  // PDF Export
  const [exporting, setExporting] = useState(false);

  // Bin batch add
  const [binPopover, setBinPopover] = useState(null); // equipment item for popover
  const [binQty, setBinQty] = useState(4);
  const BIN_GAP_MM = 50; // gap between snapped bins

  const pushHistory = useCallback((items) => {
    if (skipHistoryRef.current) { skipHistoryRef.current = false; return; }
    const h = historyRef.current.slice(0, historyIdxRef.current + 1);
    h.push(JSON.parse(JSON.stringify(items)));
    if (h.length > 50) h.shift();
    historyRef.current = h;
    historyIdxRef.current = h.length - 1;
  }, []);

  const undo = useCallback(() => {
    if (historyIdxRef.current > 0) {
      historyIdxRef.current -= 1;
      skipHistoryRef.current = true;
      setPlacedItems(JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current])));
    }
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current < historyRef.current.length - 1) {
      historyIdxRef.current += 1;
      skipHistoryRef.current = true;
      setPlacedItems(JSON.parse(JSON.stringify(historyRef.current[historyIdxRef.current])));
    }
  }, []);

  const pxPerMm = useMemo(() => {
    if (calibrationData?.pxPerMm) return calibrationData.pxPerMm;
    const scaleNum = parseInt(roomScale.split(':')[1]) || 100;
    return 1 / scaleNum * 3.78;
  }, [roomScale, calibrationData]);

  useEffect(() => { loadRoom(); loadEquipment(); loadSuppliers(); }, [roomId]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setStageSize({ width: rect.width, height: rect.height });
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Push to history when items change
  useEffect(() => { pushHistory(placedItems); }, [placedItems, pushHistory]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      if (e.key === 'Delete' || e.key === 'Backspace') { if (selectedItemId) handleDeleteItem(); }
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey) { if (selectedItemId) handleRotate(); }
      if (e.key === 'd' && !e.ctrlKey && !e.metaKey) { if (selectedItemId) handleDuplicate(); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [undo, redo, selectedItemId]);

  const loadRoom = async () => {
    try {
      const res = await axios.get(`${API}/waste-rooms/${roomId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      setRoom(data);
      setRoomScale(data.scale || '1:100');
      setRoomWidthMm(data.room_width_mm || '');
      setRoomDepthMm(data.room_depth_mm || '');
      setAnalysisFlags(data.analysis_results || []);
      setViolationItemIds(data.violation_item_ids || []);
      if (data.canvas_state?.calibration) setCalibrationData(data.canvas_state.calibration);
      const currentCalibration = data.canvas_state?.calibration;
      const currentPxPerMm = currentCalibration?.pxPerMm || (1 / (parseInt((data.scale || '1:100').split(':')[1]) || 100) * 3.78);
      const items = (data.placed_equipment || []).map(item => {
        if (item.x_mm !== undefined) return item;
        return { ...item, x_mm: (item.x || 0) / currentPxPerMm, y_mm: (item.y || 0) / currentPxPerMm, width_mm: item.width_mm || item.width || 0, height_mm: item.height_mm || item.height || 0 };
      });
      setPlacedItems(items);
      if (data.plan_image_data) {
        const img = new window.Image();
        img.src = data.plan_image_data;
        img.onload = () => setPlanImage(img);
      }
      if (!data.plan_image_data && !data.room_width_mm) setShowUpload(true);
    } catch (err) { navigate('/waste-room-builder'); }
    finally { setLoading(false); }
  };

  const loadEquipment = async () => { try { setEquipment((await axios.get(`${API}/equipment`)).data); } catch (err) { console.error(err); } };
  const loadSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/equipment/suppliers/list`);
      setSuppliers(res.data);
      if (res.data.length > 0 && !res.data.includes(selectedSupplier)) setSelectedSupplier(res.data[0]);
    } catch (err) { console.error(err); }
  };

  const handleUploadPlan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/waste-rooms/${roomId}/upload-plan`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      await loadRoom();
      setShowUpload(false);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/waste-rooms/${roomId}`, {
        room_width_mm: parseFloat(roomWidthMm) || null,
        room_depth_mm: parseFloat(roomDepthMm) || null,
        scale: roomScale,
        placed_equipment: placedItems,
        canvas_state: { calibration: calibrationData },
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const handleAnalyse = async () => {
    await handleSave();
    setAnalysing(true);
    try {
      const res = await axios.post(`${API}/waste-rooms/${roomId}/analyse`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysisFlags(res.data.flags || []);
      setViolationItemIds(res.data.violation_item_ids || []);
    } catch (err) { alert('Analysis failed'); }
    finally { setAnalysing(false); }
  };

  const addEquipmentToCanvas = (eq, qty = 1) => {
    // Place at center of current viewport
    const viewCenterXPx = (stageSize.width / 2 - position.x) / scale;
    const viewCenterYPx = (stageSize.height / 2 - position.y) / scale;
    const startXCenter = viewCenterXPx / pxPerMm;
    const startYCenter = viewCenterYPx / pxPerMm;

    if (qty === 1) {
      const newItem = {
        id: `placed-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        equipment_id: eq.id, name: eq.model_name, supplier: eq.supplier, category: eq.category,
        x_mm: startXCenter - eq.width_mm / 2, y_mm: startYCenter - eq.depth_mm / 2,
        width_mm: eq.width_mm, height_mm: eq.depth_mm, rotation: 0,
        clearance_front: eq.required_clearance_front_mm || 0, clearance_side: eq.required_clearance_side_mm || 0,
        clearance_rear: eq.required_clearance_rear_mm || 0, operation_clearance: eq.operation_clearance_mm || 0,
        min_ceiling: eq.min_ceiling_height_mm || 0, notes: eq.notes, num_bins: eq.num_bins, bin_capacity_l: eq.bin_capacity_l, num_chutes: eq.num_chutes || 0,
      };
      setPlacedItems(prev => [...prev, newItem]);
      setSelectedItemId(newItem.id);
      return;
    }

    // Grid placement for multiple bins
    const cols = Math.ceil(Math.sqrt(qty * (eq.depth_mm / eq.width_mm)));
    const rows = Math.ceil(qty / cols);
    const cellW = eq.width_mm + BIN_GAP_MM;
    const cellH = eq.depth_mm + BIN_GAP_MM;
    const gridW = cols * cellW - BIN_GAP_MM;
    const gridH = rows * cellH - BIN_GAP_MM;
    const originX = startXCenter - gridW / 2;
    const originY = startYCenter - gridH / 2;

    const newItems = [];
    for (let i = 0; i < qty; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      newItems.push({
        id: `placed-${Date.now()}-${Math.random().toString(36).slice(2, 6)}-${i}`,
        equipment_id: eq.id, name: eq.model_name, supplier: eq.supplier, category: eq.category,
        x_mm: originX + col * cellW, y_mm: originY + row * cellH,
        width_mm: eq.width_mm, height_mm: eq.depth_mm, rotation: 0,
        clearance_front: eq.required_clearance_front_mm || 0, clearance_side: eq.required_clearance_side_mm || 0,
        clearance_rear: eq.required_clearance_rear_mm || 0, operation_clearance: eq.operation_clearance_mm || 0,
        min_ceiling: eq.min_ceiling_height_mm || 0, notes: eq.notes, num_bins: eq.num_bins, bin_capacity_l: eq.bin_capacity_l, num_chutes: eq.num_chutes || 0,
      });
    }
    setPlacedItems(prev => [...prev, ...newItems]);
    setBinPopover(null);
  };

  const handleItemDrag = (itemId, target) => {
    // Group is positioned at center (due to offset), so convert back to top-left mm
    const item = placedItems.find(i => i.id === itemId);
    if (!item) return;
    const newXMm = target.x() / pxPerMm - item.width_mm / 2;
    const newYMm = target.y() / pxPerMm - item.height_mm / 2;
    setPlacedItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, x_mm: newXMm, y_mm: newYMm } : i
    ));
  };

  const handleRotate = () => {
    if (!selectedItemId) return;
    setPlacedItems(prev => prev.map(item => {
      if (item.id !== selectedItemId) return item;
      return { ...item, rotation: ((item.rotation || 0) + 90) % 360 };
    }));
  };

  const handleDeleteItem = () => {
    if (!selectedItemId) return;
    setPlacedItems(prev => prev.filter(item => item.id !== selectedItemId));
    setSelectedItemId(null);
  };

  const handleDuplicate = () => {
    if (!selectedItemId) return;
    const src = placedItems.find(i => i.id === selectedItemId);
    if (!src) return;
    const dup = { ...src, id: `placed-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, x_mm: src.x_mm + 200, y_mm: src.y_mm + 200 };
    setPlacedItems(prev => [...prev, dup]);
    setSelectedItemId(dup.id);
  };

  // Snap-to-edge during drag — enhanced for bin-to-bin Tetris snapping
  const SNAP_THRESHOLD_MM = 150;
  const handleItemDragMove = (itemId, target) => {
    const item = placedItems.find(i => i.id === itemId);
    if (!item) return;
    const rW = parseFloat(roomWidthMm) || 0;
    const rD = parseFloat(roomDepthMm) || 0;
    let cx = target.x() / pxPerMm;
    let cy = target.y() / pxPerMm;
    const isBin = item.category === 'bin';
    const snapT = isBin ? 100 : SNAP_THRESHOLD_MM;
    const gap = isBin ? BIN_GAP_MM : 0;
    const hw = item.width_mm / 2, hh = item.height_mm / 2;

    // Snap to room walls (if room dims set)
    if (rW && rD) {
      if (Math.abs(cx - hw) < snapT) cx = hw;
      if (Math.abs(cx + hw - rW) < snapT) cx = rW - hw;
      if (Math.abs(cy - hh) < snapT) cy = hh;
      if (Math.abs(cy + hh - rD) < snapT) cy = rD - hh;
    }

    // Snap to other items' edges — with gap for bins
    let snappedX = false, snappedY = false;
    placedItems.forEach(other => {
      if (other.id === itemId) return;
      const ox = other.x_mm + other.width_mm / 2, oy = other.y_mm + other.height_mm / 2;
      const ohw = other.width_mm / 2, ohh = other.height_mm / 2;
      const bothBins = isBin && other.category === 'bin';
      const g = bothBins ? gap : 0;

      // Left edge snaps to right edge (with gap)
      if (!snappedX && Math.abs((cx - hw) - (ox + ohw + g)) < snapT) { cx = ox + ohw + g + hw; snappedX = true; }
      // Right edge snaps to left edge (with gap)
      if (!snappedX && Math.abs((cx + hw) - (ox - ohw - g)) < snapT) { cx = ox - ohw - g - hw; snappedX = true; }
      // Top edge snaps to bottom edge (with gap)
      if (!snappedY && Math.abs((cy - hh) - (oy + ohh + g)) < snapT) { cy = oy + ohh + g + hh; snappedY = true; }
      // Bottom edge snaps to top edge (with gap)
      if (!snappedY && Math.abs((cy + hh) - (oy - ohh - g)) < snapT) { cy = oy - ohh - g - hh; snappedY = true; }

      // Align edges (top-to-top, left-to-left) for neat rows/columns
      if (!snappedX && Math.abs(cx - ox) < snapT * 0.5) cx = ox;
      if (!snappedY && Math.abs(cy - oy) < snapT * 0.5) cy = oy;
      // Align top edges
      if (!snappedY && Math.abs((cy - hh) - (oy - ohh)) < snapT * 0.4) cy = oy - ohh + hh;
      // Align left edges
      if (!snappedX && Math.abs((cx - hw) - (ox - ohw)) < snapT * 0.4) cx = ox - ohw + hw;
    });

    target.x(cx * pxPerMm);
    target.y(cy * pxPerMm);
  };

  // Measure tool — click two points to get distance
  const handleMeasureClick = (e) => {
    if (!measureMode) return;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    const mmX = (pointer.x - position.x) / scale / pxPerMm;
    const mmY = (pointer.y - position.y) / scale / pxPerMm;
    if (measurePoints.length < 2) {
      setMeasurePoints(prev => [...prev, { x: mmX, y: mmY }]);
    } else {
      setMeasurePoints([{ x: mmX, y: mmY }]);
    }
  };

  // Calibration tool — click two points on the plan image
  const handleCalibrateClick = (e) => {
    if (!calibrateMode) return;
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    // Store in raw pixel coords (canvas space, before mm conversion)
    const pxX = (pointer.x - position.x) / scale;
    const pxY = (pointer.y - position.y) / scale;
    if (calibratePoints.length < 2) {
      setCalibratePoints(prev => [...prev, { x: pxX, y: pxY }]);
    } else {
      setCalibratePoints([{ x: pxX, y: pxY }]);
    }
  };

  const calibratePixelDistance = calibratePoints.length === 2
    ? Math.sqrt(Math.pow(calibratePoints[1].x - calibratePoints[0].x, 2) + Math.pow(calibratePoints[1].y - calibratePoints[0].y, 2))
    : null;

  const applyCalibration = () => {
    const distMm = parseFloat(calibrateDistanceMm);
    if (!calibratePixelDistance || !distMm || distMm <= 0) return;
    const newPxPerMm = calibratePixelDistance / distMm;
    const cal = { point1: calibratePoints[0], point2: calibratePoints[1], distanceMm: distMm, pxPerMm: newPxPerMm };
    setCalibrationData(cal);
    setCalibrateMode(false);
    setCalibratePoints([]);
    setCalibrateDistanceMm('');
  };

  const clearCalibration = () => {
    setCalibrationData(null);
    setCalibratePoints([]);
    setCalibrateDistanceMm('');
    setCalibrateMode(false);
  };

  const measureDistance = measurePoints.length === 2
    ? Math.round(Math.sqrt(Math.pow(measurePoints[1].x - measurePoints[0].x, 2) + Math.pow(measurePoints[1].y - measurePoints[0].y, 2)))
    : null;

  const handleOptimise = async () => {
    if (!roomWidthMm || !roomDepthMm) { alert('Set room dimensions before optimising.'); return; }
    if (placedItems.length === 0) { alert('Place equipment before optimising.'); return; }
    await handleSave();
    setOptimising(true);
    try {
      const res = await axios.post(`${API}/waste-rooms/${roomId}/optimise`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLayouts(res.data.layouts || []);
      setShowLayouts(true);
    } catch (err) { alert('Optimisation failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setOptimising(false); }
  };

  const applyLayout = (layout) => {
    const newItems = placedItems.map(item => {
      const suggestion = layout.items.find(s => s.id === item.id);
      if (suggestion) return { ...item, x_mm: suggestion.x_mm, y_mm: suggestion.y_mm, rotation: suggestion.rotation ?? item.rotation };
      return item;
    });
    setPlacedItems(newItems);
    setShowLayouts(false);
  };

  const handleExportPdf = async () => {
    await handleSave();
    setExporting(true);
    try {
      let canvasImage = null;
      if (stageRef.current) {
        const stage = stageRef.current;
        const oldScale = stage.scaleX();
        const oldPos = stage.position();

        // Calculate bounding box of all content
        let minX = 0, minY = 0, maxX = stageSize.width, maxY = stageSize.height;
        const roomWPx = (parseFloat(roomWidthMm) || 0) * pxPerMm;
        const roomHPx = 0; // room height not separately tracked; use image or content bounds

        if (planImage) {
          const imgW = roomWPx > 0 ? roomWPx : (planImage.width || 0);
          const imgH = roomHPx > 0 ? roomHPx : (planImage.height || 0);
          maxX = Math.max(maxX, imgW);
          maxY = Math.max(maxY, imgH);
        }

        placedItems.forEach(item => {
          const px = item.x_mm * pxPerMm;
          const py = item.y_mm * pxPerMm;
          const pw = (item.width_mm || 0) * pxPerMm;
          const ph = (item.height_mm || 0) * pxPerMm;
          minX = Math.min(minX, px - 100);
          minY = Math.min(minY, py - 100);
          maxX = Math.max(maxX, px + pw + 100);
          maxY = Math.max(maxY, py + ph + 100);
        });

        if (roomWPx > 0) maxX = Math.max(maxX, roomWPx);
        if (roomHPx > 0) maxY = Math.max(maxY, roomHPx);

        const contentW = maxX - minX;
        const contentH = maxY - minY;
        const captureScale = Math.min(2, 4000 / contentW, 4000 / contentH);

        stage.scale({ x: 1, y: 1 });
        stage.position({ x: 0, y: 0 });
        stage.batchDraw();

        canvasImage = stage.toDataURL({
          x: minX,
          y: minY,
          width: contentW,
          height: contentH,
          pixelRatio: captureScale,
          mimeType: 'image/png',
        });

        stage.scale({ x: oldScale, y: oldScale });
        stage.position(oldPos);
        stage.batchDraw();
      }
      const res = await axios.post(`${API}/waste-rooms/${roomId}/export-pdf`,
        { canvas_image: canvasImage },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `WasteRoom_${(room?.name || 'export').replace(/\s/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Export failed: ' + (err.response?.data?.detail || err.message)); }
    finally { setExporting(false); }
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const oldScale = scale;
    const pointer = stageRef.current.getPointerPosition();
    const mousePointTo = { x: (pointer.x - position.x) / oldScale, y: (pointer.y - position.y) / oldScale };
    const newScale = e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;
    const clampedScale = Math.max(0.1, Math.min(5, newScale));
    setScale(clampedScale);
    setPosition({ x: pointer.x - mousePointTo.x * clampedScale, y: pointer.y - mousePointTo.y * clampedScale });
  };

  const roomWidthPx = parseFloat(roomWidthMm || 0) * pxPerMm;
  const roomDepthPx = parseFloat(roomDepthMm || 0) * pxPerMm;
  const filteredEquipment = equipment.filter(eq => selectedSupplier === '__all__' || eq.supplier === selectedSupplier)
    .sort((a, b) => {
      const order = { linear: 0, chute: 1, compactor: 2, bin: 3, carousel: 4 };
      return (order[a.category] ?? 3) - (order[b.category] ?? 3);
    });
  const selectedItem = placedItems.find(item => item.id === selectedItemId);
  const errorCount = analysisFlags.filter(f => f.level === 'error').length;
  const warningCount = analysisFlags.filter(f => f.level === 'warning').length;

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-400 text-sm">Loading workspace...</p></div>;

  return (
    <div className="h-screen flex bg-slate-50 overflow-hidden">
      <SEO title={room?.name || 'Waste Room Planner'} noIndex={true} />

      {/* ─── Left: Equipment Library ─── */}
      <div className="w-72 bg-white/95 backdrop-blur-md border-r border-slate-200 flex flex-col flex-shrink-0 overflow-hidden" data-testid="equipment-sidebar">
        <div className="px-4 pt-4 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => navigate('/waste-room-builder')} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="back-to-rooms">
              <ArrowLeft size={18} strokeWidth={1.5} />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-slate-800 truncate">{room?.name}</h2>
              <p className="text-[10px] text-slate-400">v{room?.version || 1}</p>
            </div>
          </div>
          <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} className="w-full bg-slate-50 text-slate-700 text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none" data-testid="supplier-select">
            <option value="__all__">All Suppliers</option>
            {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {filteredEquipment.map(eq => {
            const cat = CATEGORY_COLORS[eq.category] || CATEGORY_COLORS.bin;
            const isBinItem = eq.category === 'bin';
            const isPopoverOpen = binPopover?.id === eq.id;
            return (
              <div key={eq.id} className="relative">
                <div onClick={() => {
                    if (isBinItem) { setBinPopover(isPopoverOpen ? null : eq); setBinQty(4); }
                    else { addEquipmentToCanvas(eq); }
                  }}
                  className={`group flex items-center gap-2 p-2 rounded-xl border transition-all cursor-pointer active:scale-[0.98] ${isPopoverOpen ? 'border-slate-300 bg-slate-50 shadow-sm' : 'border-transparent hover:border-slate-200 hover:bg-slate-50 hover:shadow-sm'}`}
                  data-testid={`equipment-card-${eq.id}`}>
                  {/* Mini diagram */}
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: cat.bg, border: `1px solid ${cat.stroke}15` }}>
                    <EquipmentDiagram category={eq.category} width={eq.width_mm} height={eq.depth_mm} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-slate-700 truncate leading-tight">{eq.model_name}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{eq.width_mm} x {eq.depth_mm}mm</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {eq.bin_capacity_l > 0 && <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded">{eq.bin_capacity_l}L</span>}
                      {eq.num_bins > 1 && <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded">{eq.num_bins} bins</span>}
                      {eq.has_compactor && <span className="text-[9px] px-1 py-0.5 bg-amber-50 text-amber-600 rounded">Compactor</span>}
                      {isBinItem && <span className="text-[9px] px-1 py-0.5 bg-blue-50 text-blue-500 rounded">Click for qty</span>}
                    </div>
                  </div>
                </div>
                {/* Bin quantity popover */}
                {isPopoverOpen && (
                  <div className="mx-2 mb-1 p-2.5 bg-white border border-slate-200 rounded-xl shadow-lg" data-testid={`bin-qty-popover-${eq.id}`}>
                    <p className="text-[10px] text-slate-500 font-medium mb-2">How many {eq.model_name}s?</p>
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => { e.stopPropagation(); setBinQty(q => Math.max(1, q - 1)); }} className="h-7 w-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors" data-testid="bin-qty-minus">
                        <Minus size={13} />
                      </button>
                      <input type="number" value={binQty} onChange={e => setBinQty(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))} onClick={e => e.stopPropagation()}
                        className="w-12 text-center text-sm font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-lg py-1 focus:outline-none focus:border-blue-400" data-testid="bin-qty-input" />
                      <button onClick={(e) => { e.stopPropagation(); setBinQty(q => Math.min(30, q + 1)); }} className="h-7 w-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors" data-testid="bin-qty-plus">
                        <Plus size={13} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); addEquipmentToCanvas(eq, binQty); }} className="flex-1 h-7 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold transition-all flex items-center justify-center gap-1" data-testid="bin-qty-add">
                        <Plus size={12} /> Add {binQty}
                      </button>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {[1, 4, 6, 10].map(n => (
                        <button key={n} onClick={(e) => { e.stopPropagation(); setBinQty(n); }} className={`flex-1 text-[10px] py-1 rounded-md transition-colors font-medium ${binQty === n ? 'bg-blue-100 text-blue-700' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`} data-testid={`bin-preset-${n}`}>{n}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filteredEquipment.length === 0 && <p className="text-slate-400 text-xs text-center py-8">No equipment for this supplier</p>}
        </div>
      </div>

      {/* ─── Center: Canvas ─── */}
      <div className="flex-1 relative" ref={containerRef} style={{ background: 'radial-gradient(#cbd5e1 0.8px, transparent 0.8px)', backgroundSize: '20px 20px' }}>

        {/* Floating toolbar — emphasized */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-white/95 backdrop-blur-xl border border-slate-200 shadow-md rounded-2xl px-3 h-12 flex items-center gap-1.5" data-testid="planner-toolbar">
          {/* Scale */}
          {calibrationData ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <span className="text-xs font-semibold text-emerald-700">Calibrated</span>
              <button onClick={clearCalibration} className="text-emerald-400 hover:text-red-500 transition-colors" title="Remove calibration"><X size={12} /></button>
            </div>
          ) : (
            <select value={roomScale} onChange={e => setRoomScale(e.target.value)} className="bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-1.5 rounded-lg border border-slate-200 focus:outline-none cursor-pointer" data-testid="scale-select">
              {SCALES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Calibrate tool */}
          <button onClick={() => { setCalibrateMode(!calibrateMode); setCalibratePoints([]); setMeasureMode(false); }}
            className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${calibrateMode ? 'text-emerald-600 bg-emerald-50 border border-emerald-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`}
            data-testid="calibrate-scale-btn" title={planImage ? "Two-point calibration" : "Upload a plan image first"}>
            <Crosshair size={15} strokeWidth={1.5} />
            Calibrate
          </button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Dimensions */}
          <button onClick={() => setShowSetup(!showSetup)} className="text-xs text-slate-600 hover:text-blue-600 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors font-medium" data-testid="calibrate-btn">
            {roomWidthMm && roomDepthMm ? `${(roomWidthMm/1000).toFixed(1)}m x ${(roomDepthMm/1000).toFixed(1)}m` : 'Dimensions'}
          </button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Upload */}
          <label className="text-xs text-slate-600 hover:text-blue-600 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer flex items-center gap-1.5 font-medium" data-testid="upload-plan-btn">
            <Upload size={15} strokeWidth={1.5} />
            {planImage ? 'Change' : 'Upload'}
            <input type="file" accept="image/*,.pdf" onChange={handleUploadPlan} className="hidden" />
          </label>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Clearance toggle */}
          <button onClick={() => setShowClearanceZones(!showClearanceZones)} className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${showClearanceZones ? 'text-blue-600 bg-blue-50 border border-blue-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`} data-testid="toggle-clearance-btn" title="Toggle clearance zones">
            {showClearanceZones ? <Eye size={15} strokeWidth={1.5} /> : <EyeOff size={15} strokeWidth={1.5} />}
            CLR
          </button>
          {/* Measure tool */}
          <button onClick={() => { setMeasureMode(!measureMode); setMeasurePoints([]); setCalibrateMode(false); setCalibratePoints([]); }} className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all ${measureMode ? 'text-amber-600 bg-amber-50 border border-amber-200' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 border border-transparent'}`} data-testid="measure-btn" title="Measure distance">
            <Ruler size={15} strokeWidth={1.5} />
            Measure
          </button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Undo */}
          <button onClick={undo} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid="undo-btn" title="Undo (Ctrl+Z)">
            <Undo2 size={15} strokeWidth={1.5} />
          </button>
          {/* Redo */}
          <button onClick={redo} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid="redo-btn" title="Redo (Ctrl+Y)">
            <Redo2 size={15} strokeWidth={1.5} />
          </button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Rotate */}
          <button onClick={handleRotate} disabled={!selectedItemId} className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all border ${selectedItemId ? 'text-slate-700 hover:text-blue-600 hover:bg-blue-50 border-slate-200 hover:border-blue-200' : 'text-slate-300 border-transparent cursor-default'}`} data-testid="rotate-btn" title="Rotate 90deg (R)">
            <RotateCw size={15} strokeWidth={1.5} />
            Rotate
          </button>
          {/* Duplicate */}
          <button onClick={handleDuplicate} disabled={!selectedItemId} className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all border ${selectedItemId ? 'text-slate-700 hover:text-blue-600 hover:bg-blue-50 border-slate-200 hover:border-blue-200' : 'text-slate-300 border-transparent cursor-default'}`} data-testid="duplicate-btn" title="Duplicate (D)">
            <Copy size={15} strokeWidth={1.5} />
            Dupe
          </button>
          {/* Delete */}
          <button onClick={handleDeleteItem} disabled={!selectedItemId} className={`h-8 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-medium transition-all border ${selectedItemId ? 'text-red-500 hover:bg-red-50 border-red-200 hover:border-red-300' : 'text-slate-300 border-transparent cursor-default'}`} data-testid="delete-item-btn" title="Delete selected (Del)">
            <Trash2 size={15} strokeWidth={1.5} />
            Delete
          </button>
          <div className="w-px h-6 bg-slate-200 mx-0.5" />
          {/* Save */}
          <button onClick={handleSave} className="h-8 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-all active:scale-95 flex items-center gap-1.5 shadow-sm" data-testid="save-btn">
            <Save size={14} strokeWidth={1.5} />
            {saving ? 'Saving...' : 'Save'}
          </button>
          {/* Export PDF */}
          <button onClick={handleExportPdf} disabled={exporting} className="h-8 px-3 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold transition-all active:scale-95 flex items-center gap-1.5 shadow-sm" data-testid="export-pdf-btn" title="Export PDF report">
            <FileDown size={14} strokeWidth={1.5} />
            {exporting ? 'Exporting...' : 'PDF'}
          </button>
        </div>

        {/* Setup dropdown */}
        {showSetup && (
          <div className="absolute top-[68px] left-1/2 -translate-x-1/2 z-20 bg-white border border-slate-200 shadow-lg rounded-xl px-5 py-4 flex items-center gap-3" data-testid="setup-panel">
            <span className="text-xs text-slate-500 font-medium">Room size</span>
            <input type="number" value={roomWidthMm} onChange={e => setRoomWidthMm(e.target.value)} placeholder="Width mm" className="w-24 bg-slate-50 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none" data-testid="room-width-input" />
            <span className="text-slate-300 text-xs">x</span>
            <input type="number" value={roomDepthMm} onChange={e => setRoomDepthMm(e.target.value)} placeholder="Depth mm" className="w-24 bg-slate-50 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-blue-400 focus:outline-none" data-testid="room-depth-input" />
            <button onClick={() => { setShowSetup(false); handleSave(); }} className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" data-testid="apply-dimensions-btn">Apply</button>
            <button onClick={() => setShowSetup(false)} className="text-slate-400 hover:text-slate-600 text-xs">Cancel</button>
          </div>
        )}

        {/* Calibration panel */}
        {calibrateMode && (
          <div className="absolute top-[68px] left-1/2 -translate-x-1/2 z-20 bg-white border border-emerald-200 shadow-lg rounded-xl px-5 py-4 max-w-md" data-testid="calibrate-panel">
            {calibratePoints.length < 2 ? (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Crosshair size={16} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800">
                    {calibratePoints.length === 0 ? 'Click the first point on your plan' : 'Click the second point'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Choose two points with a known real-world distance</p>
                </div>
                <button onClick={() => { setCalibrateMode(false); setCalibratePoints([]); }} className="text-slate-400 hover:text-slate-600 ml-2"><X size={14} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Check size={16} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-800 mb-1.5">Enter the real distance between those points</p>
                  <div className="flex items-center gap-2">
                    <input type="number" value={calibrateDistanceMm} onChange={e => setCalibrateDistanceMm(e.target.value)} placeholder="e.g. 5000"
                      className="w-28 bg-slate-50 text-slate-700 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 focus:border-emerald-400 focus:outline-none" data-testid="calibrate-distance-input" autoFocus />
                    <span className="text-[10px] text-slate-400">mm</span>
                    <button onClick={applyCalibration} disabled={!calibrateDistanceMm || parseFloat(calibrateDistanceMm) <= 0}
                      className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40" data-testid="apply-calibration-btn">Apply</button>
                  </div>
                </div>
                <button onClick={() => { setCalibratePoints([]); setCalibrateDistanceMm(''); }} className="text-slate-400 hover:text-slate-600 ml-2 text-[10px]">Reset</button>
              </div>
            )}
          </div>
        )}

        {/* Upload overlay */}
        {showUpload && !planImage && !roomWidthMm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-10 border border-slate-200 shadow-lg max-w-sm text-center">
              <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <Upload size={24} className="text-blue-500" strokeWidth={1.5} />
              </div>
              <h3 className="text-slate-800 font-semibold text-lg mb-1">Get started</h3>
              <p className="text-slate-400 text-sm mb-6 leading-relaxed">Upload a snip of your room plan, or start with a blank canvas.</p>
              <label className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-full hover:bg-blue-700 cursor-pointer transition-all active:scale-95 shadow-sm" data-testid="initial-upload-btn">
                Upload Plan Image
                <input type="file" accept="image/*,.pdf" onChange={handleUploadPlan} className="hidden" />
              </label>
              <button onClick={() => { setShowUpload(false); setShowSetup(true); }} className="block mx-auto mt-4 text-slate-400 text-xs hover:text-blue-500 transition-colors" data-testid="skip-upload-btn">Skip — use blank canvas</button>
            </div>
          </div>
        )}

        {/* Konva Canvas */}
        <Stage ref={stageRef} width={stageSize.width} height={stageSize.height} scaleX={scale} scaleY={scale} x={position.x} y={position.y} draggable={!measureMode && !calibrateMode} onWheel={handleWheel}
          onDragEnd={(e) => { if (e.target === stageRef.current) setPosition({ x: e.target.x(), y: e.target.y() }); }}
          onClick={(e) => {
            if (calibrateMode) { handleCalibrateClick(e); return; }
            if (measureMode) { handleMeasureClick(e); return; }
            const cn = e.target.getClassName();
            const n = e.target.name ? e.target.name() : '';
            if (e.target === stageRef.current || cn === 'Image' || n === 'room-bg' || n === 'clr-zone') setSelectedItemId(null);
          }}
        >
          {/* Plan image + room boundary */}
          <Layer>
            {planImage && roomWidthPx > 0 && roomDepthPx > 0 && <KonvaImage image={planImage} x={0} y={0} width={roomWidthPx} height={roomDepthPx} opacity={0.5} />}
            {planImage && !(roomWidthPx > 0 && roomDepthPx > 0) && <KonvaImage image={planImage} x={0} y={0} opacity={0.5} />}
            {roomWidthPx > 0 && roomDepthPx > 0 && (
              <>
                <Rect name="room-bg" x={0} y={0} width={roomWidthPx} height={roomDepthPx} stroke="#94a3b8" strokeWidth={1.5} dash={[6, 4]} fill="rgba(255,255,255,0.3)" cornerRadius={2} />
                <Text x={roomWidthPx / 2 - 30} y={-16} text={`${parseFloat(roomWidthMm).toLocaleString()}mm`} fontSize={10} fill="#94a3b8" fontStyle="500" listening={false} />
                <Text x={roomWidthPx + 8} y={roomDepthPx / 2 - 5} text={`${parseFloat(roomDepthMm).toLocaleString()}mm`} fontSize={10} fill="#94a3b8" rotation={90} fontStyle="500" listening={false} />
              </>
            )}
          </Layer>

          {/* Equipment layer */}
          <Layer>
            {placedItems.map(item => {
              const isSelected = item.id === selectedItemId;
              const hasViolation = violationItemIds.includes(item.id);
              const itemCategory = item.category || (
                (item.name || '').toLowerCase().includes('carousel') ? 'carousel' :
                (item.name || '').toLowerCase().includes('linear') || (item.name || '').toLowerCase().includes('track') ? 'linear' :
                (item.name || '').toLowerCase().includes('chute') ? 'chute' :
                (item.name || '').toLowerCase().includes('compactor') ? 'compactor' : 'bin'
              );
              const cat = CATEGORY_COLORS[itemCategory] || CATEGORY_COLORS.bin;
              const isCarousel = itemCategory === 'carousel';
              const isLinear = itemCategory === 'linear';

              const wPx = (item.width_mm || 0) * pxPerMm;
              const hPx = (item.height_mm || 0) * pxPerMm;
              const clrFPx = (item.clearance_front || 0) * pxPerMm;
              const clrSPx = (item.clearance_side || 0) * pxPerMm;
              const clrRPx = (item.clearance_rear || 0) * pxPerMm;
              const radius = isCarousel ? Math.max(wPx, hPx) / 2 : 0;
              // Center position for rotation
              const centerXPx = ((item.x_mm || 0) + (item.width_mm || 0) / 2) * pxPerMm;
              const centerYPx = ((item.y_mm || 0) + (item.height_mm || 0) / 2) * pxPerMm;

              return (
                <Group key={item.id}
                  x={centerXPx} y={centerYPx}
                  offsetX={wPx / 2} offsetY={hPx / 2}
                  rotation={item.rotation || 0}
                  draggable
                  onClick={(e) => { e.cancelBubble = true; if (!measureMode) setSelectedItemId(item.id); }}
                  onTap={(e) => { e.cancelBubble = true; if (!measureMode) setSelectedItemId(item.id); }}
                  onDragMove={(e) => handleItemDragMove(item.id, e.target)}
                  onDragEnd={(e) => handleItemDrag(item.id, e.target)}
                >
                  {/* Clearance zone — inside rotatable group so it rotates with equipment */}
                  {showClearanceZones && (clrFPx > 0 || clrSPx > 0 || clrRPx > 0) && (
                    isCarousel ? (
                      <Circle name="clr-zone" x={wPx / 2} y={hPx / 2}
                        radius={radius + Math.max(clrFPx, clrSPx, clrRPx)}
                        fill={hasViolation ? 'rgba(239, 68, 68, 0.06)' : 'rgba(59, 130, 246, 0.04)'}
                        stroke={hasViolation ? 'rgba(239, 68, 68, 0.5)' : 'rgba(59, 130, 246, 0.35)'}
                        strokeWidth={1.5} dash={[5, 4]} listening={false} />
                    ) : isLinear ? (
                      /* Linear tracks: service/front clearance on LEFT, rear on RIGHT, side on TOP/BOTTOM */
                      <Rect name="clr-zone" x={-clrFPx} y={-clrSPx} width={wPx + clrFPx + clrRPx} height={hPx + clrSPx * 2}
                        fill={hasViolation ? 'rgba(239, 68, 68, 0.06)' : 'rgba(59, 130, 246, 0.04)'}
                        stroke={hasViolation ? 'rgba(239, 68, 68, 0.5)' : 'rgba(59, 130, 246, 0.35)'}
                        strokeWidth={1.5} dash={[5, 4]} listening={false} />
                    ) : (
                      <Rect name="clr-zone" x={-clrSPx} y={-clrRPx} width={wPx + clrSPx * 2} height={hPx + clrRPx + clrFPx}
                        fill={hasViolation ? 'rgba(239, 68, 68, 0.06)' : 'rgba(59, 130, 246, 0.04)'}
                        stroke={hasViolation ? 'rgba(239, 68, 68, 0.5)' : 'rgba(59, 130, 246, 0.35)'}
                        strokeWidth={1.5} dash={[5, 4]} listening={false} />
                    )
                  )}

                  {isCarousel ? (
                    (() => {
                      const cx = wPx / 2, cy = hPx / 2;
                      const numBins = item.num_bins || 4;
                      const cap = item.bin_capacity_l || 240;
                      const binW = cap >= 1100 ? radius * 0.44 : cap >= 660 ? radius * 0.40 : radius * 0.32;
                      const binH = cap >= 1100 ? radius * 0.38 : cap >= 660 ? radius * 0.34 : radius * 0.36;
                      const binDist = radius * 0.55;
                      const binCorner = Math.min(binW, binH) * 0.18;
                      const handleW = Math.max(1.5, binW * 0.06);
                      const handleH = Math.max(2, binH * 0.35);
                      const strokeColor = isSelected ? '#3b82f6' : hasViolation ? '#ef4444' : cat.stroke;
                      return <>
                        <Circle x={cx} y={cy} radius={radius}
                          fill={isSelected ? 'rgba(59, 130, 246, 0.04)' : hasViolation ? 'rgba(239, 68, 68, 0.03)' : 'rgba(255,255,255,0.9)'}
                          stroke={strokeColor} strokeWidth={isSelected ? 2.5 : 1.5}
                          shadowColor={isSelected ? '#3b82f6' : 'rgba(0,0,0,0.06)'}
                          shadowBlur={isSelected ? 10 : 4} shadowOpacity={isSelected ? 0.3 : 0.5} shadowOffsetY={isSelected ? 0 : 1}
                        />
                        <Circle x={cx} y={cy} radius={Math.max(3, radius * 0.12)}
                          fill="transparent" stroke={strokeColor} strokeWidth={1} opacity={0.4} listening={false} />
                        <Circle x={cx} y={cy} radius={Math.max(1.5, radius * 0.04)}
                          fill={strokeColor} opacity={0.3} listening={false} />
                        {Array.from({ length: numBins }).map((_, bi) => {
                          const angle = (360 / numBins) * bi - 90;
                          const rad = angle * Math.PI / 180;
                          const bx = cx + binDist * Math.cos(rad);
                          const by = cy + binDist * Math.sin(rad);
                          return (
                            <Group key={`bin-${bi}`}>
                              <Rect x={bx} y={by} offsetX={binW / 2} offsetY={binH / 2}
                                width={binW} height={binH} rotation={angle + 90}
                                fill="transparent" stroke={strokeColor} strokeWidth={0.8}
                                cornerRadius={binCorner} opacity={0.5} listening={false} />
                              <Rect x={bx} y={by}
                                offsetX={binW / 2 + handleW} offsetY={handleH / 2}
                                width={handleW} height={handleH} rotation={angle + 90}
                                fill={strokeColor} opacity={0.2} cornerRadius={0.5} listening={false} />
                              <Rect x={bx} y={by}
                                offsetX={-binW / 2} offsetY={handleH / 2}
                                width={handleW} height={handleH} rotation={angle + 90}
                                fill={strokeColor} opacity={0.2} cornerRadius={0.5} listening={false} />
                            </Group>
                          );
                        })}
                        {Array.from({ length: numBins }).map((_, bi) => {
                          const angle = (360 / numBins) * bi - 90;
                          const rad = angle * Math.PI / 180;
                          const hubR = Math.max(4, radius * 0.12);
                          const spokeEnd = binDist - binH / 2 - 1;
                          return <Line key={`spoke-${bi}`}
                            points={[cx + hubR * Math.cos(rad), cy + hubR * Math.sin(rad), cx + spokeEnd * Math.cos(rad), cy + spokeEnd * Math.sin(rad)]}
                            stroke={strokeColor} strokeWidth={0.6} opacity={0.2} listening={false} />;
                        })}
                      </>;
                    })()
                  ) : (
                    <>
                      <Rect x={0} y={0} width={wPx} height={hPx}
                        fill={isSelected ? 'rgba(59, 130, 246, 0.08)' : hasViolation ? 'rgba(239, 68, 68, 0.06)' : 'rgba(255,255,255,0.9)'}
                        stroke={isSelected ? '#3b82f6' : hasViolation ? '#ef4444' : cat.stroke}
                        strokeWidth={isSelected ? 2.5 : 1.5} cornerRadius={3}
                        shadowColor={isSelected ? '#3b82f6' : 'rgba(0,0,0,0.06)'}
                        shadowBlur={isSelected ? 10 : 4} shadowOpacity={isSelected ? 0.3 : 0.5} shadowOffsetY={isSelected ? 0 : 1}
                      />
                      {/* Chute: circle(s) inside rectangle representing chute opening(s) */}
                      {itemCategory === 'chute' && wPx > 5 && (() => {
                        const numChutes = item.num_chutes || (/dual|diverter/i.test(item.name) ? 2 : 1);
                        const strokeCol = isSelected ? '#3b82f6' : hasViolation ? '#ef4444' : cat.stroke;
                        if (numChutes >= 2) {
                          const chuteR = Math.min(wPx / 2, hPx) * 0.34;
                          const spacing = wPx / 2;
                          return <>
                            <Circle x={spacing / 2 + chuteR * 0.1} y={hPx / 2} radius={chuteR}
                              fill="transparent" stroke={strokeCol} strokeWidth={1.2} opacity={0.6} listening={false} />
                            <Circle x={spacing / 2 + chuteR * 0.1} y={hPx / 2} radius={Math.max(2, chuteR * 0.15)}
                              fill={strokeCol} opacity={0.3} listening={false} />
                            <Circle x={wPx - spacing / 2 - chuteR * 0.1} y={hPx / 2} radius={chuteR}
                              fill="transparent" stroke={strokeCol} strokeWidth={1.2} opacity={0.6} listening={false} />
                            <Circle x={wPx - spacing / 2 - chuteR * 0.1} y={hPx / 2} radius={Math.max(2, chuteR * 0.15)}
                              fill={strokeCol} opacity={0.3} listening={false} />
                          </>;
                        }
                        const chuteR = Math.min(wPx, hPx) * 0.38;
                        return <>
                          <Circle x={wPx / 2} y={hPx / 2} radius={chuteR}
                            fill="transparent" stroke={strokeCol} strokeWidth={1.2} opacity={0.6} listening={false} />
                          <Circle x={wPx / 2} y={hPx / 2} radius={Math.max(2, chuteR * 0.15)}
                            fill={strokeCol} opacity={0.3} listening={false} />
                        </>;
                      })()}
                      {/* Compactor: dashed outline to indicate ceiling-mounted */}
                      {itemCategory === 'compactor' && wPx > 5 && (() => {
                        const strokeCol = isSelected ? '#3b82f6' : hasViolation ? '#ef4444' : cat.stroke;
                        const arrowSize = Math.min(8, Math.min(wPx, hPx) * 0.2);
                        return <>
                          <Rect x={2} y={2} width={wPx - 4} height={hPx - 4}
                            fill="transparent" stroke={strokeCol} strokeWidth={0.8}
                            dash={[3, 2]} opacity={0.4} cornerRadius={2} listening={false} />
                          <RegularPolygon x={wPx / 2} y={hPx * 0.3} sides={3} radius={arrowSize}
                            fill={strokeCol} opacity={0.4} listening={false} />
                          <RegularPolygon x={wPx / 2} y={hPx * 0.7} sides={3} radius={arrowSize} rotation={180}
                            fill={strokeCol} opacity={0.4} listening={false} />
                        </>;
                      })()}
                      {isLinear && wPx > 10 && (() => {
                        const numBins = item.num_bins || 2;
                        const strokeCol = isSelected ? '#3b82f6' : hasViolation ? '#ef4444' : cat.stroke;
                        const padX = wPx * 0.06;
                        const padY = hPx * 0.08;
                        const gap = Math.max(1, wPx * 0.008);
                        const binH = hPx - padY * 2;
                        const totalBinW = wPx - padX * 2 - gap * (numBins - 1);
                        const binW = totalBinW / numBins;
                        const binR = Math.min(binW, binH) * 0.06;
                        const arrowSize = Math.min(6, hPx * 0.12);
                        return <>
                          <Rect x={2} y={1} width={wPx - 4} height={1.5} fill={strokeCol} opacity={0.12} listening={false} />
                          <Rect x={2} y={hPx - 2.5} width={wPx - 4} height={1.5} fill={strokeCol} opacity={0.12} listening={false} />
                          {Array.from({ length: numBins }).map((_, i) => {
                            const bx = padX + i * (binW + gap);
                            return (
                              <Group key={`lbin-${i}`}>
                                <Rect x={bx} y={padY} width={binW} height={binH}
                                  fill="transparent" stroke={strokeCol} strokeWidth={0.8}
                                  cornerRadius={binR} opacity={0.5} listening={false} />
                                <Rect x={bx - 1} y={padY + binH * 0.35}
                                  width={Math.max(1.5, 2)} height={binH * 0.3}
                                  fill={strokeCol} opacity={0.2} cornerRadius={0.5} listening={false} />
                              </Group>
                            );
                          })}
                          <RegularPolygon x={Math.max(arrowSize + 2, padX * 0.5)} y={hPx / 2}
                            sides={3} radius={arrowSize} rotation={-90}
                            fill={strokeCol} opacity={0.4} listening={false} />
                        </>;
                      })()}
                    </>
                  )}

                  {/* Labels — counter-rotated so they always read horizontally */}
                  {wPx > 20 && (
                    <Text x={wPx / 2} y={hPx / 2 - 5}
                      offsetX={Math.min(wPx * 0.35, 60)} offsetY={0}
                      rotation={-(item.rotation || 0)}
                      text={item.name} fontSize={Math.min(10, Math.max(wPx, hPx) / 5)} fill={hasViolation ? '#ef4444' : cat.label}
                      width={Math.min(wPx * 0.7, 120)} align="center"
                      ellipsis wrap="none" listening={false} fontStyle="600" />
                  )}
                  {!isCarousel && wPx > 30 && hPx > 22 && (
                    <Text x={wPx / 2} y={hPx / 2 + 6}
                      offsetX={25} offsetY={0}
                      rotation={-(item.rotation || 0)}
                      text={`${Math.round(item.width_mm)}x${Math.round(item.height_mm)}`} fontSize={8} fill="#94a3b8" listening={false} width={50} align="center" />
                  )}
                </Group>
              );
            })}
          </Layer>

          {/* Measure line layer */}
          {measureMode && measurePoints.length > 0 && (
            <Layer>
              {measurePoints.map((pt, i) => (
                <Circle key={`mp-${i}`} x={pt.x * pxPerMm} y={pt.y * pxPerMm} radius={4 / scale} fill="#f59e0b" stroke="#fff" strokeWidth={1 / scale} />
              ))}
              {measurePoints.length === 2 && (
                <>
                  <Line points={[measurePoints[0].x * pxPerMm, measurePoints[0].y * pxPerMm, measurePoints[1].x * pxPerMm, measurePoints[1].y * pxPerMm]}
                    stroke="#f59e0b" strokeWidth={1.5 / scale} dash={[6 / scale, 3 / scale]} />
                  <Text
                    x={(measurePoints[0].x + measurePoints[1].x) / 2 * pxPerMm}
                    y={(measurePoints[0].y + measurePoints[1].y) / 2 * pxPerMm - 16 / scale}
                    text={`${measureDistance}mm`}
                    fontSize={12 / scale} fill="#f59e0b" fontStyle="bold" align="center"
                    offsetX={30 / scale} width={60 / scale}
                  />
                </>
              )}
            </Layer>
          )}
          {/* Calibrate line layer */}
          {calibrateMode && calibratePoints.length > 0 && (
            <Layer>
              {calibratePoints.map((pt, i) => (
                <Circle key={`cp-${i}`} x={pt.x} y={pt.y} radius={5 / scale} fill="#10b981" stroke="#fff" strokeWidth={1.5 / scale} />
              ))}
              {calibratePoints.length === 2 && (
                <>
                  <Line points={[calibratePoints[0].x, calibratePoints[0].y, calibratePoints[1].x, calibratePoints[1].y]}
                    stroke="#10b981" strokeWidth={2 / scale} dash={[6 / scale, 3 / scale]} />
                  <Text
                    x={(calibratePoints[0].x + calibratePoints[1].x) / 2}
                    y={(calibratePoints[0].y + calibratePoints[1].y) / 2 - 18 / scale}
                    text={calibrateDistanceMm ? `${calibrateDistanceMm}mm` : `${Math.round(calibratePixelDistance)}px`}
                    fontSize={12 / scale} fill="#10b981" fontStyle="bold" align="center"
                    offsetX={30 / scale} width={60 / scale}
                  />
                </>
              )}
            </Layer>
          )}
        </Stage>

        {/* Zoom controls + measure indicator */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 z-20">
          <div className="bg-white/95 backdrop-blur-sm border border-slate-200 shadow-sm rounded-xl p-1 flex items-center gap-0.5">
            <button onClick={() => setScale(s => Math.min(5, s * 1.2))} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid="zoom-in-btn"><ZoomIn size={16} strokeWidth={1.5} /></button>
            <span className="text-slate-400 text-[11px] px-2 font-medium min-w-[44px] text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.max(0.1, s / 1.2))} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid="zoom-out-btn"><ZoomOut size={16} strokeWidth={1.5} /></button>
            <div className="w-px h-5 bg-slate-200" />
            <button onClick={() => { setScale(1); setPosition({ x: 0, y: 0 }); }} className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="zoom-reset-btn"><Maximize size={14} strokeWidth={1.5} /></button>
          </div>
          {measureMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5 text-xs text-amber-700 font-medium" data-testid="measure-indicator">
              {measureDistance ? `${measureDistance.toLocaleString()}mm` : measurePoints.length === 1 ? 'Click second point...' : 'Click to measure'}
            </div>
          )}
          {calibrateMode && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-1.5 text-xs text-emerald-700 font-medium" data-testid="calibrate-indicator">
              {calibratePoints.length === 0 ? 'Click first point...' : calibratePoints.length === 1 ? 'Click second point...' : 'Enter distance above'}
            </div>
          )}
        </div>
      </div>

      {/* ─── Right: Analysis Panel ─── */}
      <div className="w-80 bg-white/95 backdrop-blur-md border-l border-slate-200 flex flex-col flex-shrink-0 overflow-hidden" data-testid="analysis-panel">
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Analysis</h3>
          {(errorCount > 0 || warningCount > 0) && (
            <div className="flex items-center gap-1.5">
              {errorCount > 0 && <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-500 rounded-full font-semibold" data-testid="error-count">{errorCount}</span>}
              {warningCount > 0 && <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-semibold" data-testid="warning-count">{warningCount}</span>}
            </div>
          )}
        </div>

        {/* Summary Metrics */}
        {(roomWidthMm && roomDepthMm) && (() => {
          const rW = parseFloat(roomWidthMm) || 0;
          const rD = parseFloat(roomDepthMm) || 0;
          const roomArea = rW * rD / 1e6; // m²
          const eqFootprint = placedItems.reduce((sum, item) => {
            const rot = (item.rotation || 0) % 360;
            const w = (rot === 90 || rot === 270) ? item.height_mm : item.width_mm;
            const h = (rot === 90 || rot === 270) ? item.width_mm : item.height_mm;
            return sum + (w * h) / 1e6;
          }, 0);
          const freeArea = Math.max(0, roomArea - eqFootprint);
          const usagePct = roomArea > 0 ? Math.round((eqFootprint / roomArea) * 100) : 0;
          const hasErrors = errorCount > 0;
          const hasWarnings = warningCount > 0;
          return (
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50" data-testid="room-metrics">
              <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-[11px]">
                <div>
                  <p className="text-slate-400">Room area</p>
                  <p className="text-slate-700 font-semibold">{roomArea.toFixed(1)} m²</p>
                </div>
                <div>
                  <p className="text-slate-400">Equipment</p>
                  <p className="text-slate-700 font-semibold">{eqFootprint.toFixed(1)} m² ({usagePct}%)</p>
                </div>
                <div>
                  <p className="text-slate-400">Free space</p>
                  <p className="text-slate-700 font-semibold">{freeArea.toFixed(1)} m²</p>
                </div>
                <div>
                  <p className="text-slate-400">Status</p>
                  <p className={`font-semibold ${hasErrors ? 'text-red-500' : hasWarnings ? 'text-amber-600' : analysisFlags.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {hasErrors ? 'Issues found' : hasWarnings ? 'Warnings' : analysisFlags.length > 0 ? 'Compliant' : 'Not analysed'}
                  </p>
                </div>
              </div>
              {calibrationData && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                    <Crosshair size={10} /> Calibrated: {calibrationData.distanceMm}mm reference
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {selectedItem && (
          <div className="px-5 py-3 border-b border-slate-100 bg-blue-50/50" data-testid="selected-item-info">
            <h4 className="text-[13px] font-semibold text-slate-800 mb-0.5">{selectedItem.name}</h4>
            <p className="text-[11px] text-slate-400 mb-2">{selectedItem.supplier}</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
              <span className="text-slate-400">Size</span><span className="text-slate-700 font-medium">{Math.round(selectedItem.width_mm)} x {Math.round(selectedItem.height_mm)}mm</span>
              <span className="text-slate-400">Position</span><span className="text-slate-700 font-medium">{Math.round(selectedItem.x_mm)}, {Math.round(selectedItem.y_mm)}</span>
              {selectedItem.num_bins > 0 && <><span className="text-slate-400">Bins</span><span className="text-slate-700 font-medium">{selectedItem.num_bins} x {selectedItem.bin_capacity_l}L</span></>}
              {selectedItem.clearance_front > 0 && <><span className="text-slate-400">{selectedItem.category === 'linear' ? 'Service CLR' : 'Front CLR'}</span><span className="text-slate-700 font-medium">{selectedItem.clearance_front}mm</span></>}
              {selectedItem.clearance_side > 0 && <><span className="text-slate-400">Side CLR</span><span className="text-slate-700 font-medium">{selectedItem.clearance_side}mm</span></>}
              {selectedItem.min_ceiling > 0 && <><span className="text-slate-400">Min ceiling</span><span className="text-slate-700 font-medium">{selectedItem.min_ceiling}mm</span></>}
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-b border-slate-100">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Placed ({placedItems.length})</p>
          {placedItems.length === 0 ? (
            <p className="text-slate-300 text-xs py-2">Click equipment to add</p>
          ) : (
            <div className="space-y-0.5 max-h-28 overflow-y-auto">
              {placedItems.map(item => {
                const hasV = violationItemIds.includes(item.id);
                return (
                  <div key={item.id} onClick={() => setSelectedItemId(item.id)}
                    className={`flex items-center justify-between text-[11px] px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${item.id === selectedItemId ? 'bg-blue-50 text-blue-700 font-medium' : hasV ? 'bg-red-50/60 text-red-500' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`bom-item-${item.id}`}>
                    <span className="truncate flex items-center gap-1.5">
                      {hasV && <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />}
                      {item.name}
                    </span>
                    <span className="text-slate-300 text-[10px] flex-shrink-0 ml-2">{Math.round(item.width_mm)}x{Math.round(item.height_mm)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <button onClick={handleAnalyse} className="w-full mb-3 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-all active:scale-[0.97] shadow-sm flex items-center justify-center gap-2" data-testid="analyse-btn">
            {analysing ? (<><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Analysing...</>) : 'Analyse Layout'}
          </button>
          <button onClick={handleOptimise} disabled={optimising} className="w-full mb-4 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-xs font-semibold rounded-xl transition-all active:scale-[0.97] shadow-sm flex items-center justify-center gap-2" data-testid="optimise-btn">
            {optimising ? (<><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Optimising...</>) : (<><Sparkles size={14} strokeWidth={1.5} />AI Optimise Layout</>)}
          </button>

          {/* AI Layouts suggestions */}
          {showLayouts && layouts && layouts.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="ai-layouts-panel">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold text-violet-600 uppercase tracking-wide">AI Suggestions</p>
                <button onClick={() => setShowLayouts(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
              </div>
              {layouts.map((layout, idx) => (
                <div key={idx} className="p-3 rounded-xl border border-violet-100 bg-violet-50/50 hover:bg-violet-50 transition-colors" data-testid={`ai-layout-${idx}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-slate-800">{layout.name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{layout.description}</p>
                    </div>
                    <button onClick={() => applyLayout(layout)} className="flex-shrink-0 px-2.5 py-1 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[10px] font-semibold transition-all flex items-center gap-1" data-testid={`apply-layout-${idx}`}>
                      <Check size={11} />Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {analysisFlags.length > 0 && (
            <div className="space-y-2">
              {analysisFlags.filter(f => f.level === 'error').map((flag, i) => (
                <div key={`e-${i}`} className="p-3 rounded-xl bg-red-50 border border-red-100 text-[11px] text-red-600 leading-relaxed" data-testid={`analysis-error-${i}`}>{flag.message}</div>
              ))}
              {analysisFlags.filter(f => f.level === 'warning').map((flag, i) => (
                <div key={`w-${i}`} className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-[11px] text-amber-700 leading-relaxed" data-testid={`analysis-warning-${i}`}>{flag.message}</div>
              ))}
              {analysisFlags.filter(f => f.level === 'success').map((flag, i) => (
                <div key={`s-${i}`} className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-[11px] text-emerald-700 leading-relaxed" data-testid={`analysis-success-${i}`}>{flag.message}</div>
              ))}
              {analysisFlags.filter(f => f.level === 'info').map((flag, i) => (
                <div key={`i-${i}`} className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-[11px] text-slate-500 leading-relaxed" data-testid={`analysis-info-${i}`}>{flag.message}</div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100">
          <p className="text-slate-300 text-[10px] italic">Layout guidance only — not certified engineering output.</p>
        </div>
      </div>
    </div>
  );
};

export default WasteRoomPlanner;
